import React, { useEffect, useState } from 'react';
import { Box, Button, Avatar, Dialog, DialogContent, ToggleButton, ToggleButtonGroup, Typography, CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { green, grey, red } from '@mui/material/colors';
import { TEAM_ICON_DEFAULT } from '../../../../common/staticData';

import MatchAll from '../../../../assets/match_all.png';
import SeasonContext from '../../../../common/Context';
import { PLAYER_ICON_DEFAULT } from '../../../../common/staticData';
import GameService from '../../../../services/game.service';
import { ActionData, isGoalTag } from '../../components/common';
import TeamStatsVideoPlayer from '../../teams/tabs/stats/videoDialog';
import GameExportToEdits from '../../games/tabs/overview/exportEdits';
import { getPeriod } from '../../games/tabs/overview/tagListItem';
import PlayerStatErrorMessage from '../../players/status/errorMessage';
import PlayerEditDialog from '../../players/playerEditDialog';
import redcard from '../../../../assets/images/redcard.jpeg';
import yellowcard from '../../../../assets/images/yellowcard.jpeg';
import { mapPlayerStatsData } from '../../../../common/utilities';

// Alias for backward compatibility within this file
const mapStatsData = mapPlayerStatsData;

// Use exact SQL column names - these have both total_X and average_X calculated
// All fields returned from filtered_complete SQL function
export const statList = [
    // Core stats
    { id: 'goals', title: 'Goals', action: 'Goal' },
    { id: 'assists', title: 'Assists', action: 'Assist' },
    { id: 'shots', title: 'Shots', action: 'GoalKick' },
    { id: 'shots_on_target', title: 'Shots On Target', action: 'ShotOnTarget' },
    { id: 'shots_off_target', title: 'Shots Off Target', action: 'ShotOffTarget' },
    { id: 'shots_in_the_box', title: 'Shots In The Box', action: 'GoalKick' },
    { id: 'shots_out_of_the_box', title: 'Shots Out Of The Box', action: 'GoalKick' },
    { id: 'penalties', title: 'Penalties', action: 'Penalty' },
    { id: 'penalties_missed', title: 'Penalties Missed', action: 'PenaltyMissed' },
    { id: 'own_goals', title: 'Own Goals', action: 'Goal' },
    // Dribbles
    { id: 'dribbles', title: 'Dribbles', action: 'Dribble' },
    { id: 'dribbles_success', title: 'Successful Dribbles', action: 'DribbleSuccess' },
    // Crosses & Set pieces
    { id: 'crosses', title: 'Crosses', action: 'Cross' },
    { id: 'crosses_success', title: 'Successful Crosses', action: 'CrossSuccess' },
    { id: 'free_kicks', title: 'Free Kicks', action: 'FreeKick' },
    { id: 'corner', title: 'Corners', action: 'Corner' },
    // Passes
    { id: 'passes', title: 'Passes', action: 'Passes' },
    { id: 'passes_success', title: 'Successful Passes', action: 'PassesSuccess' },
    { id: 'passes_for_shots', title: 'Passes For Shots', action: 'PassesShots' },
    { id: 'key_passes', title: 'Key Passes', action: 'KeyPass' },
    { id: 'key_passes_successful', title: 'Successful Key Passes', action: 'KeyPass' },
    { id: 'through_passes', title: 'Through Passes', action: 'ThroughPass' },
    { id: 'through_passes_success', title: 'Successful Through Passes', action: 'ThroughPass' },
    { id: 'short_passes', title: 'Short Passes', action: 'ShortPass' },
    { id: 'long_passes', title: 'Long Passes', action: 'LongPass' },
    { id: 'long_passes_success', title: 'Successful Long Passes', action: 'LongPassSuccess' },
    { id: 'freekick_passes', title: 'Free Kick Passes', action: 'FreeKickPass' },
    { id: 'throwin_passes', title: 'Throw-In Passes', action: 'ThrowIn' },
    // Turnovers
    { id: 'turnovers', title: 'Turnovers', action: 'Turnover' },
    { id: 'turnovers_on_offensive_court', title: 'Turnovers Offensive', action: 'TurnoverOffensive' },
    { id: 'turnovers_on_defensive_court', title: 'Turnovers Defensive', action: 'TurnoverDefensive' },
    { id: 'offsides', title: 'Offsides', action: 'Offside' },
    // Defensive stats
    { id: 'tackles', title: 'Tackles', action: 'Tackle' },
    { id: 'tackles_success', title: 'Successful Tackles', action: 'Tackle' },
    { id: 'tackle_on_offensive_court', title: 'Tackles Offensive', action: 'TackleOffensive' },
    { id: 'tackle_on_defensive_court', title: 'Tackles Defensive', action: 'TackleDefensive' },
    { id: 'interceptions', title: 'Interceptions', action: 'Interception' },
    { id: 'interception_on_offensive_court', title: 'Interceptions Offensive', action: 'InterceptionOffensive' },
    { id: 'interception_on_defensive_court', title: 'Interceptions Defensive', action: 'InterceptionDefensive' },
    { id: 'clearances', title: 'Clearances', action: 'Clearance' },
    { id: 'blocks', title: 'Blocks', action: 'Blocked' },
    { id: 'saved', title: 'Saved', action: 'Saved' },
    // Fouls & Cards
    { id: 'fouls', title: 'Fouls', action: 'Foul' },
    { id: 'draw_fouls', title: 'Draw Fouls', action: 'DrawFoul' },
    { id: 'yellow_cards', title: 'Yellow Cards', action: 'YellowCard' },
    { id: 'red_cards', title: 'Red Cards', action: 'RedCard' },
    // Goalkeeper stats (only calculated for position_id = 16)
    { id: 'super_saves', title: 'Super Saves', action: 'SuperSaved' },
    { id: 'build_ups', title: 'Build Ups', action: 'BuildUp' },
    { id: 'goalkeeper_exit', title: 'GK Exits', action: 'Exits' },
    { id: 'air_challenge', title: 'Air Challenges', action: 'AirChallenge' },
    { id: 'ground_challenge', title: 'Ground Challenges', action: 'GroundChallenge' },
    { id: 'one_vs_one', title: '1 vs 1', action: 'One' },
    { id: 'goals_received', title: 'Goals Received', action: 'GoalReceive' },
    { id: 'goal_kicks', title: 'Goal Kicks', action: 'GoalKick' },
    { id: 'punches', title: 'Punches', action: 'Saved' },
    { id: 'catches', title: 'Catches', action: 'Saved' },
    { id: 'throws', title: 'Throws', action: 'ThrowIn' },
    { id: 'opponent_crosses', title: 'Opponent Crosses', action: 'OpponentCross' },
    { id: 'opponent_corners', title: 'Opponent Corners', action: 'OpponentCorner' },
    { id: 'opponent_free_kicks', title: 'Opponent Free Kicks', action: 'OpponentFreeKick' },
    // Games
    { id: 'player_games', title: 'Games', action: '' }
];

export const goalkeeper = [
    { id: 'passes', title: 'Passes', action: 'Passes' },
    { id: 'passes_success', title: 'Successful Passes', action: 'PassesSuccess' },
    { id: 'short_passes', title: 'Short Passes', action: 'ShortPass' },
    { id: 'long_passes', title: 'Long Passes', action: 'LongPass' },
    { id: 'build_ups', title: 'Build Ups', action: 'BuildUp' },
    { id: 'super_saves', title: 'Super Saves', action: 'SuperSaved' },
    { id: 'saved', title: 'Saved', action: 'Saved' },
    { id: 'goalkeeper_exit', title: 'Exits', action: 'Exits' },
    { id: 'air_challenge', title: 'Air Challenges', action: 'AirChallenge' },
    { id: 'ground_challenge', title: 'Ground Challenges', action: 'GroundChallenge' },
    { id: 'one_vs_one', title: '1 vs 1', action: 'One' },
    { id: 'goals_received', title: 'Goals Received', action: 'GoalReceive' },
    { id: 'tackles', title: 'Tackles', action: 'Tackle' },
    { id: 'interceptions', title: 'Interceptions', action: 'Interception' },
    { id: 'clearances', title: 'Clearances', action: 'Clearance' },
    { id: 'fouls', title: 'Fouls', action: 'Foul' },
    { id: 'draw_fouls', title: 'Draw Fouls', action: 'DrawFoul' },
    { id: 'red_cards', title: 'Red Cards', action: 'RedCard' },
    { id: 'yellow_cards', title: 'Yellow Cards', action: 'YellowCard' },
    { id: 'player_games', title: 'Games Played', action: '' }
];

const PlayerStatDialog = ({ open, onClose, player, games, isOur = true, gameIds, initialState, statsData, updatePlayer = () => {}, t }) => {
    const { seasonFilter } = React.useContext(SeasonContext);

    const [playerState, setPlayerState] = useState(null);
    const [gameHalf, setGameHalf] = useState(['first', 'second']);
    const [gameTime, setGameTime] = useState(['1', '2', '3', '4', '5', '6']);
    const [loading, setLoading] = useState(false);
    const [loadingStatId, setLoadingStatId] = useState(null);
    const [courtArea, setCourtArea] = useState(['1', '2', '3', '4']);
    const [errorOpen, setErrorOpen] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [gameResult, setGameResult] = useState(null);
    const [gamePlace, setGamePlace] = useState(null);
    const [playData, setPlayData] = useState([]);
    const [gameList, setGameList] = useState([]);
    const [filteredGameIds, setFilteredGameIds] = useState(null);
    const [videoOpen, setVideoOpen] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);
    const [refresh, setRefresh] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editPlayerData, setEditPlayerData] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [currentPlayer, setCurrentPlayer] = useState({});

    const arr_stat_profile = ['player_games', 'yellow_cards', 'red_cards'];

    // Goalkeeper-only stats - hide these for non-goalkeeper players
    const goalkeeperOnlyStats = ['build_ups', 'goalkeeper_exit', 'opponent_crosses', 'opponent_corners', 'opponent_free_kicks'];

    const { user: currentUser } = useSelector((state) => state.auth);

    const handleChangeGameHalf = (e, newHalf) => {
        setGameHalf(newHalf);
        if (newHalf.length === 2) {
            setGameTime(['1', '2', '3', '4', '5', '6']);
        } else if (newHalf.length === 1) {
            if (newHalf.includes('first')) {
                const newList = gameTime.filter((item) => item === '4' || item === '5' || item === '6');
                setGameTime(newList.length === 3 ? ['1', '2', '3'] : [...newList, '1', '2', '3']);
            } else if (newHalf.includes('second')) {
                const newList = gameTime.filter((item) => item === '1' || item === '2' || item === '3');
                setGameTime(newList.length === 3 ? ['4', '5', '6'] : [...newList, '4', '5', '6']);
            }
        } else {
            if (e.target.innerText === '1 HALF') {
                setGameTime(gameTime.filter((item) => item === '4' || item === '5' || item === '6'));
            } else if (e.target.innerText === '2 HALF') {
                setGameTime(gameTime.filter((item) => item === '1' || item === '2' || item === '3'));
            }
        }
    };

    const handleChangeGameTime = (e, newTime) => {
        setGameTime(newTime);
        if (newTime.length === 6) {
            setGameHalf(['first', 'second']);
        } else if (newTime.length >= 0 && newTime.length <= 2) {
            setGameHalf([]);
        } else if (newTime.length >= 3 && newTime.length <= 5) {
            const diff1 = ['1', '2', '3'].filter((item) => !newTime.includes(item));
            const diff2 = ['4', '5', '6'].filter((item) => !newTime.includes(item));
            if (diff1.length === 0) setGameHalf(['first']);
            if (diff2.length === 0) setGameHalf(['second']);
            if (diff1.length !== 0 && diff2.length !== 0) setGameHalf([]);
        }
    };

    const handleChangeGameResult = (e, newResult) => {
        setGameResult(newResult);
    };

    const handleChangeGamePlace = (e, newPlace) => {
        setGamePlace(newPlace);
    };

    const getPlayerStat = (id) => {
        if (id === 'player_games') return playerState[`total_${id}`];
        return playerState[`total_games_player_played`] > 1 ? playerState[`total_${id}`] + ' (' + playerState[`average_${id}`] + ')' : playerState[`total_${id}`];
    };

    const handleChangeCourtArea = (courtId) => {
        const newList = courtArea.includes(courtId) ? courtArea.filter((item) => item !== courtId) : [...courtArea, courtId];
        setCourtArea(newList);
    };

    const handlePlayerStat = () => {
        if (gameTime.length === 0 || courtArea.length === 0) {
            setErrorText('');
            setErrorOpen(true);
            return;
        }

        setLoading(true);
        const fetchData = async () => {
            try {

                // Filter games based on gameResult and gamePlace filters
                let filteredGames = gameList;

                if (gameResult) {
                    const resultValue = parseInt(gameResult);
                    filteredGames = filteredGames.filter((game) => {
                        const isHomeTeam = game.home_team_id === currentPlayer.team_id;
                        const homeGoals = parseInt(game.home_team_goals) || parseInt(game.home_team_goal) || 0;
                        const awayGoals = parseInt(game.away_team_goals) || parseInt(game.away_team_goal) || 0;

                        if (resultValue === 1) { // Won
                            return isHomeTeam ? homeGoals > awayGoals : awayGoals > homeGoals;
                        } else if (resultValue === 2) { // Draw
                            return homeGoals === awayGoals;
                        } else if (resultValue === 3) { // Lost
                            return isHomeTeam ? homeGoals < awayGoals : awayGoals < homeGoals;
                        }
                        return true;
                    });
                }

                if (gamePlace) {
                    const placeValue = parseInt(gamePlace);
                    filteredGames = filteredGames.filter((game) => {
                        if (placeValue === 1) { // Home
                            return game.home_team_id === currentPlayer.team_id;
                        } else if (placeValue === 2) { // Away
                            return game.away_team_id === currentPlayer.team_id;
                        }
                        return true;
                    });
                }

                // Update filtered game IDs
                const newFilteredGameIds = filteredGames.length > 0 ? filteredGames.map(g => g.id) : (gameIds || []);
                setFilteredGameIds(newFilteredGameIds);


                const filterParams = {
                    seasonId: seasonFilter === 'none' ? null : seasonFilter.id,
                    leagueId: null,
                    gameId: newFilteredGameIds.length > 0 ? newFilteredGameIds.join(',') : null,
                    teamId: currentPlayer.team_id,
                    playerId: currentPlayer.player_id,
                    gameTime: gameTime.length === 6 ? null : gameTime.join(','),
                    courtAreaId: courtArea.length === 4 ? null : courtArea.join(','),
                    insidePaint: null,
                    homeAway: gamePlace ? parseInt(gamePlace) : null,
                    gameResult: gameResult ? parseInt(gameResult) : null
                };

                // Use unified filtered function for all player types (including goalkeepers)
                const res = await GameService.getPlayersStatsFiltered(filterParams);

                if (res && res.length > 0) {
                    const mappedStats = mapStatsData(res[0]);
                    // Preserve original team data if API response doesn't include it
                    const preservedTeamData = {
                        team_name: mappedStats.team_name || playerState?.team_name || currentPlayer?.team_name || '',
                        team_image: mappedStats.team_image || playerState?.team_image || currentPlayer?.team_image || '',
                        // Preserve team statistics if not in API response
                        team_total_games:
                            mappedStats.team_total_games !== undefined && mappedStats.team_total_games !== 0
                                ? mappedStats.team_total_games
                                : playerState?.team_total_games || currentPlayer?.team_total_games || 0,
                        games_selected:
                            mappedStats.games_selected !== undefined && mappedStats.games_selected !== 0
                                ? mappedStats.games_selected
                                : playerState?.games_selected || currentPlayer?.games_selected || 0,
                        total_games_won:
                            mappedStats.total_games_won !== undefined && mappedStats.total_games_won !== 0
                                ? mappedStats.total_games_won
                                : playerState?.total_games_won || currentPlayer?.total_games_won || 0,
                        total_games_draw:
                            mappedStats.total_games_draw !== undefined && mappedStats.total_games_draw !== 0
                                ? mappedStats.total_games_draw
                                : playerState?.total_games_draw || currentPlayer?.total_games_draw || 0,
                        total_games_lose:
                            mappedStats.total_games_lose !== undefined && mappedStats.total_games_lose !== 0
                                ? mappedStats.total_games_lose
                                : playerState?.total_games_lose || currentPlayer?.total_games_lose || 0
                    };
                    setPlayerState({ ...mappedStats, ...preservedTeamData });
                }
                setLoading(false);
            } catch (error) {
                console.error(error);
                setLoading(false);
            }
        };
        fetchData();
    };

    const handleDisplayVideo = (cell) => async (e) => {
        if (playerState && playerState[`total_${cell.id}`] > 0 && cell.title !== 'Games') {
            setLoadingStatId(cell.id);
            try {
                const actionData = ActionData[cell.action];
                if (!actionData) {
                    console.error('Action data not found for:', cell.action);
                    setLoadingStatId(null);
                    return;
                }

                // Use filtered game IDs if available, otherwise use original gameIds
                const effectiveGameIds = filteredGameIds !== null ? filteredGameIds : gameIds;

                console.log('Display Video - Current filter state:', {
                    originalGameIds: gameIds,
                    filteredGameIds,
                    effectiveGameIds,
                    gameTime,
                    courtArea,
                    gameResult,
                    gamePlace,
                    stat: cell.title
                });

                // Pass game IDs to filter by specific game(s)
                // Use actionData.court_area if specified (for court-specific stats), otherwise use UI filter
                const effectiveCourtArea = actionData.court_area || (courtArea.length === 4 ? null : courtArea.join(','));

                const res = await GameService.getGamePlayerTags(
                    currentUser.id,
                    currentPlayer.team_id,
                    `${currentPlayer.player_id}`,
                    effectiveGameIds && effectiveGameIds.length > 0 ? effectiveGameIds.join(',') : null,
                    actionData.action_id,
                    actionData.action_type_id,
                    actionData.action_result_id,
                    gameTime.length === 6 ? null : gameTime.join(','),
                    effectiveCourtArea,
                    null,
                    gameResult ? parseInt(gameResult) : null,
                    gamePlace ? parseInt(gamePlace) : null,
                    actionData.useOrLogic ? 1 : 0,
                    0  // isOpponent: 0 = team's actions (default)
                );


                if (!res || res.length === 0) {
                    setErrorText(`No clips found for ${cell.title} with the selected filters.`);
                    setErrorOpen(true);
                    setLoadingStatId(null);
                    return;
                }

                const data =
                    cell.action === 'Goal'
                        ? res.filter(isGoalTag)  // shots only (assists are also tagged result=Goal)
                        : cell.title === 'Shots In The Box'
                        ? res.filter((item) => item.inside_the_paint === true)
                        : cell.title === 'Shots Out Of The Box'
                        ? res.filter((item) => item.inside_the_paint === false)
                        : res;

                const newData = data.map((item) => ({
                    tag_id: item.id,
                    start_time: item.player_tag_start_time,
                    end_time: item.player_tag_end_time,
                    player_name: item.player_names || item.player_name || item.full_name || '',
                    action_name: item.action_names || item.action_name || '',
                    action_type: item.action_type_names || item.action_type_name || item.action_type || '',
                    action_result: item.action_result_names || item.action_result_name || item.action_result || '',
                    game_id: item.game_id,
                    team_id: currentPlayer.team_id,
                    court_area: item.court_area_id,
                    inside_pain: item.inside_the_paint,
                    period: item.period,
                    time_in_game: item.time_in_game,
                    home_team_logo: item.home_team_logo,
                    away_team_logo: item.away_team_logo,
                    home_team_goals: item.home_team_goals ?? item.home_team_goal,
                    away_team_goals: item.away_team_goals ?? item.away_team_goal
                }));

                setPlayData(newData);
                setVideoOpen(true);
                setLoadingStatId(null);
            } catch (error) {
                console.error('Error fetching video tags:', error);
                setLoadingStatId(null);
            }
        }
    };

    const handleExportTags = (cell) => async (e) => {
        e.preventDefault();
        if (playerState && playerState[`total_${cell.id}`] > 0 && cell.title !== 'Games') {
            try {
                // Use filtered game IDs if available, otherwise use original gameIds
                const effectiveGameIds = filteredGameIds !== null ? filteredGameIds : gameIds;

                const actionData = ActionData[cell.action];
                // Use actionData.court_area if specified (for court-specific stats), otherwise use UI filter
                const effectiveCourtArea = actionData.court_area || (courtArea.length === 4 ? null : courtArea.join(','));

                console.log('Export Tags - Current filter state:', {
                    originalGameIds: gameIds,
                    filteredGameIds,
                    effectiveGameIds,
                    gameTime,
                    courtArea,
                    effectiveCourtArea,
                    gameResult,
                    gamePlace,
                    stat: cell.title
                });

                // Pass game IDs to filter by specific game(s)
                const res = await GameService.getGamePlayerTags(
                    currentUser.id,
                    currentPlayer.team_id,
                    `${currentPlayer.player_id}`,
                    effectiveGameIds && effectiveGameIds.length > 0 ? effectiveGameIds.join(',') : null,
                    actionData.action_id,
                    actionData.action_type_id,
                    actionData.action_result_id,
                    gameTime.length === 6 ? null : gameTime.join(','),
                    effectiveCourtArea,
                    null,
                    gameResult ? parseInt(gameResult) : null,
                    gamePlace ? parseInt(gamePlace) : null,
                    actionData.useOrLogic ? 1 : 0,
                    0  // isOpponent: 0 = team's actions (default)
                );


                const data =
                    cell.action === 'Goal'
                        ? res.filter(isGoalTag)  // shots only (assists are also tagged result=Goal)
                        : cell.title === 'Shots In The Box'
                        ? res.filter((item) => item.inside_the_paint === true)
                        : cell.title === 'Shots Out Of The Box'
                        ? res.filter((item) => item.inside_the_paint === false)
                        : res;

                setPlayData(data);
                setExportOpen(true);
            } catch (error) {
                console.error(error);
            }
        }
    };

    const handleUpdatePlayer = (updatedPlayer) => {
        setCurrentPlayer(updatedPlayer);
        setPlayerState((prevState) => ({
            ...prevState,
            ...updatedPlayer
        }));
        // Update parent's Redux store
        updatePlayer(updatedPlayer);
    };

    const handleEditClick = async () => {
        const playerId = player?.player_id || player?.id;
        if (!playerId) {
            setEditOpen(true);
            return;
        }
        setEditLoading(true);
        try {
            const fullPlayerData = await GameService.getPlayerById(playerId);
            setEditPlayerData(fullPlayerData);
            setEditOpen(true);
        } catch (error) {
            console.error('Error fetching player details:', error);
            // Fallback to opening with existing data
            setEditPlayerData(null);
            setEditOpen(true);
        } finally {
            setEditLoading(false);
        }
    };

    // Helper function to calculate team results from games
    const calculateTeamResults = (gamesData, teamId, teamName) => {
        if (!gamesData || gamesData.length === 0 || (!teamId && !teamName)) {
            return { total_games_won: 0, total_games_draw: 0, total_games_lose: 0, team_total_games: 0 };
        }

        let wins = 0;
        let draws = 0;
        let losses = 0;
        let totalGames = 0;

        gamesData.forEach((game) => {
            // Check if this team played in this game
            const isHomeTeam = game.home_team_id === teamId || game.home_team_name === teamName;
            const isAwayTeam = game.away_team_id === teamId || game.away_team_name === teamName;

            if (!isHomeTeam && !isAwayTeam) return;

            totalGames++;

            const homeGoals = parseInt(game.home_team_goals) || parseInt(game.home_team_goal) || 0;
            const awayGoals = parseInt(game.away_team_goals) || parseInt(game.away_team_goal) || 0;

            if (homeGoals === awayGoals) {
                draws++;
            } else if (isHomeTeam) {
                if (homeGoals > awayGoals) wins++;
                else losses++;
            } else if (isAwayTeam) {
                if (awayGoals > homeGoals) wins++;
                else losses++;
            }
        });

        return { total_games_won: wins, total_games_draw: draws, total_games_lose: losses, team_total_games: totalGames };
    };

    useEffect(() => {
        setPlayerState(initialState ? mapStatsData(initialState) : initialState);
        setGameHalf(['first', 'second']);
        setGameTime(['1', '2', '3', '4', '5', '6']);
        setCourtArea(['1', '2', '3', '4']);
        setGameResult(null);
        setGamePlace(null);
        setFilteredGameIds(null);
    }, [initialState, open]);

    useEffect(() => {
        if (games && games.length) {
            const filteredGames = games.filter((item) => gameIds && gameIds.includes(item.id));
            setGameList(filteredGames);

            // Calculate team results from games if we have team info
            if (player && (player.team_id || player.team_name)) {
                const teamResults = calculateTeamResults(filteredGames, player.team_id, player.team_name);

                // Update playerState with calculated team results if they're currently 0
                setPlayerState((prevState) => {
                    if (!prevState) return prevState;

                    // Only update if current values are 0 or undefined
                    const needsUpdate =
                        (!prevState.total_games_won || prevState.total_games_won === 0) &&
                        (!prevState.total_games_draw || prevState.total_games_draw === 0) &&
                        (!prevState.total_games_lose || prevState.total_games_lose === 0);

                    if (needsUpdate && teamResults.team_total_games > 0) {
                        return {
                            ...prevState,
                            ...teamResults,
                            games_selected: teamResults.team_total_games
                        };
                    }
                    return prevState;
                });
            }
        } else if (!games || games.length === 0) {
            if (gameIds && gameIds.length > 0) {
                setGameList(gameIds.map((id) => ({ id })));
            }
        }
    }, [games, gameIds, player]);

    useEffect(() => {
        setCurrentPlayer({});
        setLoading(true);
        if (player && Object.keys(player).length !== 0) {
            const playerId = player.player_id || player.id;

            // Fetch full player profile data to get all fields (age_group, height, weight, roles, etc.)
            const fetchPlayerProfile = async () => {
                try {
                    const fullPlayerData = await GameService.getPlayerById(playerId);

                    // IMPORTANT: For multi-team players, get the team they played for in the selected games
                    // This ensures stats are filtered correctly when a player has played for multiple teams
                    let correctTeamId = player.team_id || player.teamId;
                    let correctTeamName = player.team_name;

                    if (gameIds && gameIds.length > 0) {
                        try {
                            const teamsForGames = await GameService.getPlayerTeamForGames(playerId, gameIds);
                            if (teamsForGames && teamsForGames.length > 0) {
                                // Use the team with most games in the selection
                                correctTeamId = teamsForGames[0].team_id;
                                correctTeamName = teamsForGames[0].team_name || correctTeamName;
                            }
                        } catch (teamError) {
                            console.warn('Could not fetch team for games, using default:', teamError);
                        }
                    }

                    // Fallback to fullPlayerData.team_id only if no team_id from props or games
                    if (!correctTeamId) {
                        correctTeamId = fullPlayerData.team_id;
                        correctTeamName = fullPlayerData.team_name;
                    }

                    // Merge fetched data with player prop
                    const playerData = {
                        ...player,
                        ...fullPlayerData,
                        player_full_name: fullPlayerData.player_full_name || player.player_full_name || `${player.player_first_name || ''} ${player.player_last_name || ''}`.trim(),
                        player_jersey_number: fullPlayerData.player_jersey_number || player.player_jersey_number || player.jersey_number,
                        player_image: fullPlayerData.player_image || player.player_image || player.image,
                        player_id: playerId,
                        team_id: correctTeamId,
                        team_name: correctTeamName || fullPlayerData.team_name,
                        team_image: player.team_image || fullPlayerData.team_image,
                        player_position_id: fullPlayerData.player_position_id || player.player_position_id || player.position_id,
                        player_position_name: fullPlayerData.player_position_name || player.player_position_name || player.pos_name,
                        player_first_name: fullPlayerData.player_first_name || player.player_first_name || player.first_name,
                        player_last_name: fullPlayerData.player_last_name || player.player_last_name || player.last_name,
                        player_date_of_birth: fullPlayerData.player_date_of_birth || player.player_date_of_birth || player.date_of_birth,
                        player_weight: fullPlayerData.player_weight || player.player_weight || player.weight,
                        player_height: fullPlayerData.player_height || player.player_height || player.height,
                        player_strong_foot: fullPlayerData.player_strong_foot || player.player_strong_foot || player.strong_foot,
                        // Second position and roles
                        player_second_position_name: fullPlayerData.player_second_position_name || player.player_second_position_name || player.second_position_name,
                        player_position_role_one_name: fullPlayerData.player_position_role_one_name || player.player_position_role_one_name || player.position_role_one_name,
                        player_position_role_two_name: fullPlayerData.player_position_role_two_name || player.player_position_role_two_name || player.position_role_two_name,
                        // Age group mapping
                        player_age_group_id: fullPlayerData.player_age_group_id || player.player_age_group_id || player.age_group_id,
                        player_age_group_name: fullPlayerData.player_age_group_name || player.player_age_group_name || player.age_group_name
                    };

                    setCurrentPlayer(playerData);
                    return playerData;
                } catch (error) {
                    console.error('Error fetching player profile:', error);
                    // Fallback to using player prop data
                    const playerData = {
                        ...player,
                        player_full_name: player.player_full_name || `${player.player_first_name || ''} ${player.player_last_name || ''}`.trim(),
                        player_jersey_number: player.player_jersey_number || player.jersey_number,
                        player_image: player.player_image || player.image,
                        player_id: playerId,
                        team_id: player.team_id || player.teamId,
                        team_name: player.team_name,
                        team_image: player.team_image,
                        player_position_id: player.player_position_id || player.position_id,
                        player_position_name: player.player_position_name || player.pos_name,
                        player_first_name: player.player_first_name || player.first_name,
                        player_last_name: player.player_last_name || player.last_name,
                        player_date_of_birth: player.player_date_of_birth || player.date_of_birth,
                        player_weight: player.player_weight || player.weight,
                        player_height: player.player_height || player.height,
                        player_strong_foot: player.player_strong_foot || player.strong_foot,
                        player_second_position_name: player.player_second_position_name || player.second_position_name,
                        player_position_role_one_name: player.player_position_role_one_name || player.position_role_one_name,
                        player_position_role_two_name: player.player_position_role_two_name || player.position_role_two_name,
                        player_age_group_id: player.player_age_group_id || player.age_group_id,
                        player_age_group_name: player.player_age_group_name || player.age_group_name
                    };
                    setCurrentPlayer(playerData);
                    return playerData;
                }
            };

            fetchPlayerProfile().then(async (playerData) => {
                // Calculate team results from games if available
                let teamResults = { total_games_won: 0, total_games_draw: 0, total_games_lose: 0, team_total_games: 0 };
                if (games && games.length > 0) {
                    const filteredGames = gameIds ? games.filter((item) => gameIds.includes(item.id)) : games;
                    teamResults = calculateTeamResults(filteredGames, playerData.team_id, player.team_name);
                }

                // Fetch fresh stats using the filtered function
                try {
                    const filterParams = {
                        seasonId: seasonFilter === 'none' ? null : seasonFilter.id,
                        leagueId: null,
                        gameId: gameIds && gameIds.length > 0 ? gameIds.join(',') : null,
                        teamId: playerData.team_id,
                        playerId: playerData.player_id,
                        gameTime: null, // All time periods on initial load
                        courtAreaId: null, // All court areas on initial load
                        insidePaint: null,
                        homeAway: null,
                        gameResult: null
                    };

                    // Use unified filtered function for all player types (including goalkeepers)
                    const res = await GameService.getPlayersStatsFiltered(filterParams);

                    if (res && res.length > 0) {
                        const mappedState = mapStatsData(res[0]);
                        const finalState = {
                            ...mappedState,
                            total_games_won: mappedState.total_games_won || teamResults.total_games_won,
                            total_games_draw: mappedState.total_games_draw || teamResults.total_games_draw,
                            total_games_lose: mappedState.total_games_lose || teamResults.total_games_lose,
                            team_total_games: mappedState.team_total_games || teamResults.team_total_games,
                            games_selected: mappedState.games_selected || teamResults.team_total_games
                        };
                        setPlayerState(finalState);
                    } else {
                        // Fallback to initialState/statsData if API returns no results
                        if (initialState) {
                            const mappedState = mapStatsData(initialState);
                            const finalState = {
                                ...mappedState,
                                total_games_won: mappedState.total_games_won || teamResults.total_games_won,
                                total_games_draw: mappedState.total_games_draw || teamResults.total_games_draw,
                                total_games_lose: mappedState.total_games_lose || teamResults.total_games_lose,
                                team_total_games: mappedState.team_total_games || teamResults.team_total_games,
                                games_selected: mappedState.games_selected || teamResults.team_total_games
                            };
                            setPlayerState(finalState);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching filtered stats on load:', error);
                    // Fallback to initialState/statsData on error
                    if (initialState) {
                        const mappedState = mapStatsData(initialState);
                        const finalState = {
                            ...mappedState,
                            total_games_won: mappedState.total_games_won || teamResults.total_games_won,
                            total_games_draw: mappedState.total_games_draw || teamResults.total_games_draw,
                            total_games_lose: mappedState.total_games_lose || teamResults.total_games_lose,
                            team_total_games: mappedState.team_total_games || teamResults.team_total_games,
                            games_selected: mappedState.games_selected || teamResults.team_total_games
                        };
                        setPlayerState(finalState);
                    }
                }
                setLoading(false);
            });
        }
    }, [player, editOpen, statsData, initialState, games, gameIds]);

    // Section header component for consistent styling
    const SectionHeader = ({ title, action, showClose, onCloseClick }) => (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
            paddingBottom: '12px',
            borderBottom: '2px solid #f0f0f0'
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Box sx={{
                    width: '4px',
                    height: '20px',
                    background: 'linear-gradient(180deg, #6366f1 0%, #8b5cf6 100%)',
                    borderRadius: '2px'
                }} />
                <Typography sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '15px',
                    fontWeight: 700,
                    color: '#1a1b1d',
                    letterSpacing: '0.5px'
                }}>
                    {title}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {action}
                {showClose && (
                    <Box
                        onClick={onCloseClick}
                        sx={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            background: '#fef2f2',
                            transition: 'all 0.2s ease',
                            '&:hover': { background: '#fee2e2' }
                        }}
                    >
                        <CloseIcon sx={{ color: '#ef4444', fontSize: '18px' }} />
                    </Box>
                )}
            </Box>
        </Box>
    );

    // Info row component for profile details
    const InfoRow = ({ label, value }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
            <Typography sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '13px',
                fontWeight: 500,
                color: '#64748b',
                minWidth: '110px'
            }}>
                {label}
            </Typography>
            <Typography sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '13px',
                fontWeight: 600,
                color: '#1e293b'
            }}>
                {value || '-'}
            </Typography>
        </Box>
    );

    // Minutes stat row component
    const MinutesStat = ({ label, value }) => (
        <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 0',
            borderBottom: '1px solid #f1f5f9'
        }}>
            <Typography sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '13px',
                fontWeight: 500,
                color: '#475569'
            }}>
                {label}
            </Typography>
            <Typography sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '13px',
                fontWeight: 700,
                color: '#1e293b'
            }}>
                {value}
            </Typography>
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} fullScreen PaperProps={{ style: { backgroundColor: '#f8fafc' } }}>
            <DialogContent sx={{ padding: '20px', display: 'flex', overflow: 'hidden', justifyContent: 'center', alignItems: 'center' }}>
                <Box sx={{
                    display: 'flex',
                    gap: '20px',
                    maxWidth: '1500px',
                    width: '100%',
                    height: 'calc(100vh - 40px)',
                    margin: '0 auto',
                    overflow: 'auto',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': { display: 'none' }
                }}>
                {/* Always show modal structure, only show loading in stats section */}
                <>
                {/* Left Column - Profile & Filters */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    width: '460px',
                    minWidth: '460px',
                    flexShrink: 0,
                    overflow: 'auto',
                    '&::-webkit-scrollbar': { width: '4px' },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: '#e2e8f0', borderRadius: '2px' }
                }}>
                    {/* Profile Card - Compact */}
                    <Box sx={{
                        background: '#ffffff',
                        borderRadius: '12px',
                        padding: '14px 16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        flexShrink: 0
                    }}>
                        {/* Header with Edit button */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Box sx={{ width: '4px', height: '16px', background: 'linear-gradient(180deg, #6366f1 0%, #8b5cf6 100%)', borderRadius: '2px' }} />
                                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: 700, color: '#1a1b1d' }}>{t('PROFILE')}</Typography>
                            </Box>
                            {isOur && (
                                <Button
                                    variant="contained"
                                    disabled={editLoading}
                                    onClick={handleEditClick}
                                    size="small"
                                    sx={{
                                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                        borderRadius: '6px',
                                        padding: '4px 12px',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        minWidth: 'auto',
                                        '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }
                                    }}
                                >
                                    {editLoading ? '...' : t('Edit')}
                                </Button>
                            )}
                        </Box>

                        {/* Player Header */}
                        <Box sx={{ display: 'flex', gap: '12px', marginBottom: '10px' }}>
                            <Box sx={{
                                width: '80px',
                                height: '96px',
                                borderRadius: '10px',
                                overflow: 'hidden',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                flexShrink: 0
                            }}>
                                <img
                                    src={
                                        currentPlayer?.player_image
                                            ? currentPlayer?.player_image.includes(process.env.REACT_APP_S3_URI)
                                                ? currentPlayer?.player_image
                                                : process.env.REACT_APP_S3_URI + currentPlayer?.player_image
                                            : PLAYER_ICON_DEFAULT
                                    }
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    alt="Player"
                                />
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
                                    {currentPlayer?.player_first_name ?? ''} {currentPlayer?.player_last_name ?? ''}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
                                    <Box sx={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: '4px' }}>
                                        <Typography sx={{ fontSize: '10px', color: '#64748b' }}>#{currentPlayer?.player_jersey_number ?? '-'}</Typography>
                                    </Box>
                                    <Box sx={{ background: '#ede9fe', padding: '2px 8px', borderRadius: '4px' }}>
                                        <Typography sx={{ fontSize: '10px', color: '#7c3aed', fontWeight: 600 }}>{currentPlayer?.player_position_name ?? '-'}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <Box sx={{ background: '#f8fafc', border: '2px solid #e2e8f0', padding: '4px 12px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>{!loading ? (playerState ? playerState['total_games_player_played'] : '0') : '-'}</Typography>
                                        <Typography sx={{ fontSize: '10px', color: '#64748b', fontWeight: 500, textTransform: 'uppercase' }}>{t('Games')}</Typography>
                                    </Box>
                                    {!loading && playerState && Number(playerState['total_yellow_cards']) > 0 && (
                                        <Box sx={{ backgroundImage: `url(${yellowcard})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover', color: '#fff', fontSize: '12px', fontWeight: 700, padding: '4px 8px 12px 10px', textAlign: 'center' }}>
                                            {playerState['total_yellow_cards']}
                                        </Box>
                                    )}
                                    {!loading && playerState && Number(playerState['total_red_cards']) > 0 && (
                                        <Box sx={{ backgroundImage: `url(${redcard})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover', color: '#fff', fontSize: '12px', fontWeight: 700, padding: '4px 8px 12px 10px', textAlign: 'center' }}>
                                            {playerState['total_red_cards']}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        {/* Player Details Grid - Compact */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px', background: '#f8fafc', borderRadius: '8px', padding: '8px 12px' }}>
                            <InfoRow label={t('Birthday')} value={currentPlayer?.player_date_of_birth
                                ? ((date) => {
                                    const day = date.getDate().toString().padStart(2, '0');
                                    const month = (date.getMonth() + 1).toString().padStart(2, '0');
                                    const year = date.getFullYear();
                                    return `${day}/${month}/${year}`;
                                })(new Date(currentPlayer.player_date_of_birth))
                                : ''} />
                            <InfoRow label={t('Age Group')} value={currentPlayer?.player_age_group_name} />
                            <InfoRow label={t('Height')} value={currentPlayer?.player_height ? `${parseInt(currentPlayer?.player_height)} cm` : ''} />
                            <InfoRow label={t('Weight')} value={currentPlayer?.player_weight ? `${parseInt(currentPlayer?.player_weight)} kg` : ''} />
                            <InfoRow label={t('Foot')} value={currentPlayer?.player_strong_foot} />
                            <InfoRow label={t('Role')} value={currentPlayer?.player_position_role_one_name} />
                            <InfoRow label={t('2nd Pos')} value={currentPlayer?.player_second_position_name} />
                            <InfoRow label={t('2nd Role')} value={currentPlayer?.player_position_role_two_name} />
                        </Box>
                    </Box>

                    {/* Filters Card */}
                    <Box sx={{
                        background: '#ffffff',
                        borderRadius: '16px',
                        padding: '20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)'
                    }}>
                        <SectionHeader
                            title={t('FILTERS')}
                            action={
                                <Button
                                    variant="contained"
                                    onClick={handlePlayerStat}
                                    disabled={loading}
                                    sx={{
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)',
                                        borderRadius: '8px',
                                        padding: '6px 16px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        minWidth: '100px',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                            boxShadow: '0 4px 8px rgba(16, 185, 129, 0.4)'
                                        },
                                        '&:disabled': {
                                            background: '#e2e8f0',
                                            color: '#94a3b8'
                                        }
                                    }}
                                >
                                    {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('Apply')}
                                </Button>
                            }
                        />

                        {/* Match Period */}
                        <Box sx={{ marginBottom: '16px' }}>
                            <Typography sx={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: '11px',
                                fontWeight: 600,
                                color: '#64748b',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                marginBottom: '8px'
                            }}>
                                {t('Match Period')}
                            </Typography>
                            <ToggleButtonGroup
                                color="primary"
                                fullWidth
                                size="small"
                                value={gameHalf}
                                onChange={handleChangeGameHalf}
                                sx={{
                                    marginBottom: '6px',
                                    '& .MuiToggleButton-root': {
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px !important',
                                        margin: '0 2px',
                                        padding: '6px 12px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: '#64748b',
                                        textTransform: 'none',
                                        '&.Mui-selected': {
                                            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                            color: '#fff',
                                            borderColor: 'transparent'
                                        }
                                    }
                                }}
                            >
                                <ToggleButton value="first">{t('1st Half')}</ToggleButton>
                                <ToggleButton value="second">{t('2nd Half')}</ToggleButton>
                            </ToggleButtonGroup>
                            <ToggleButtonGroup
                                color="primary"
                                fullWidth
                                size="small"
                                value={gameTime}
                                onChange={handleChangeGameTime}
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '6px !important',
                                        margin: '0 1px',
                                        padding: '4px 8px',
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        color: '#64748b',
                                        '&.Mui-selected': {
                                            background: '#6366f1',
                                            color: '#fff',
                                            borderColor: 'transparent'
                                        }
                                    }
                                }}
                            >
                                <ToggleButton value="1">1-15'</ToggleButton>
                                <ToggleButton value="2">16-30'</ToggleButton>
                                <ToggleButton value="3">31-45'+</ToggleButton>
                                <ToggleButton value="4">46-60'</ToggleButton>
                                <ToggleButton value="5">61-75'</ToggleButton>
                                <ToggleButton value="6">76-90'+</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        {/* Court Area & Game Filters - All in one row */}
                        <Box sx={{ display: 'flex', gap: '12px', alignItems: 'stretch' }}>
                            <Box sx={{
                                height: '120px',
                                width: '260px',
                                flexShrink: 0,
                                borderRadius: '8px',
                                background: `url(${MatchAll}) center center / contain no-repeat`,
                                backgroundColor: '#e8f5e9',
                                display: 'flex',
                                overflow: 'hidden',
                                boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)'
                            }}>
                                {['4', '3', '2', '1'].map((court, index) => (
                                    <Box
                                        key={`${index}-${court}`}
                                        onClick={() => handleChangeCourtArea(court)}
                                        sx={{
                                            flex: 1,
                                            cursor: 'pointer',
                                            background: courtArea.includes(court) ? 'transparent' : 'rgba(100, 116, 139, 0.6)',
                                            borderRight: index < 3 ? '1px solid rgba(255,255,255,0.3)' : 'none',
                                            transition: 'background 0.2s ease',
                                            '&:hover': {
                                                background: courtArea.includes(court) ? 'rgba(99, 102, 241, 0.1)' : 'rgba(100, 116, 139, 0.4)'
                                            }
                                        }}
                                    />
                                ))}
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <Box>
                                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '9px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '3px' }}>
                                        {t('Game Result')}
                                    </Typography>
                                    <ToggleButtonGroup
                                        disabled={loading || gameIds?.length === 1}
                                        exclusive
                                        color="primary"
                                        fullWidth
                                        size="small"
                                        value={gameResult}
                                        onChange={handleChangeGameResult}
                                        sx={{
                                            '& .MuiToggleButton-root': {
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '5px !important',
                                                margin: '0 1px',
                                                padding: '3px 4px',
                                                fontSize: '10px',
                                                fontWeight: 500,
                                                color: '#64748b',
                                                '&.Mui-selected': { background: '#6366f1', color: '#fff', borderColor: 'transparent' }
                                            }
                                        }}
                                    >
                                        <ToggleButton value="1">{t('Won')}</ToggleButton>
                                        <ToggleButton value="2">{t('Draw')}</ToggleButton>
                                        <ToggleButton value="3">{t('Lose')}</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '9px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: '3px' }}>
                                        {t('Game Place')}
                                    </Typography>
                                    <ToggleButtonGroup
                                        disabled={loading || gameIds?.length === 1}
                                        exclusive
                                        color="primary"
                                        fullWidth
                                        size="small"
                                        value={gamePlace}
                                        onChange={handleChangeGamePlace}
                                        sx={{
                                            '& .MuiToggleButton-root': {
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '5px !important',
                                                margin: '0 1px',
                                                padding: '3px 4px',
                                                fontSize: '10px',
                                                fontWeight: 500,
                                                color: '#64748b',
                                                '&.Mui-selected': { background: '#6366f1', color: '#fff', borderColor: 'transparent' }
                                            }
                                        }}
                                    >
                                        <ToggleButton value="1">{t('Home')}</ToggleButton>
                                        <ToggleButton value="2">{t('Away')}</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Right Column - Team Results, Minutes, Stats */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1, minWidth: 0 }}>
                    {/* Team Results Card - Compact */}
                    <Box sx={{
                        background: '#ffffff',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        flexShrink: 0
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <Box sx={{ width: '4px', height: '16px', background: 'linear-gradient(180deg, #6366f1 0%, #8b5cf6 100%)', borderRadius: '2px' }} />
                                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: 700, color: '#1a1b1d' }}>{t('TEAM RESULTS')}</Typography>
                                </Box>
                                <Box sx={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    background: '#f8fafc',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <img
                                        src={
                                            !loading
                                                ? playerState && playerState['team_image'] && playerState['team_image'].trim() !== ''
                                                    ? playerState['team_image'].includes(process.env.REACT_APP_S3_URI)
                                                        ? playerState['team_image']
                                                        : process.env.REACT_APP_S3_URI + playerState['team_image']
                                                    : TEAM_ICON_DEFAULT
                                                : TEAM_ICON_DEFAULT
                                        }
                                        style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                                        alt="Team"
                                    />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>
                                        {!loading ? (playerState?.team_name || currentPlayer?.team_name || '') : ''}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: '12px' }}>
                                        <Typography sx={{ fontSize: '11px', color: '#64748b' }}>
                                            Total: <span style={{ fontWeight: 700, color: '#1e293b' }}>{!loading ? (playerState ? playerState['team_total_games'] : '0') : ''}</span>
                                        </Typography>
                                        <Typography sx={{ fontSize: '11px', color: '#64748b' }}>
                                            Selected: <span style={{ fontWeight: 700, color: '#6366f1' }}>{!loading ? (playerState ? playerState['games_selected'] : '0') : ''}</span>
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f0fdf4', padding: '6px 10px', borderRadius: '8px' }}>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#16a34a' }}>
                                        {!loading ? (playerState ? playerState['total_games_won'] : '0') : '0'}
                                    </Typography>
                                    <Avatar sx={{ bgcolor: '#16a34a', width: 22, height: 22, fontSize: '10px', fontWeight: 700 }}>W</Avatar>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f8fafc', padding: '6px 10px', borderRadius: '8px' }}>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#64748b' }}>
                                        {!loading ? (playerState ? playerState['total_games_draw'] : '0') : '0'}
                                    </Typography>
                                    <Avatar sx={{ bgcolor: '#64748b', width: 22, height: 22, fontSize: '10px', fontWeight: 700 }}>D</Avatar>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fef2f2', padding: '6px 10px', borderRadius: '8px' }}>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#dc2626' }}>
                                        {!loading ? (playerState ? playerState['total_games_lose'] : '0') : '0'}
                                    </Typography>
                                    <Avatar sx={{ bgcolor: '#dc2626', width: 22, height: 22, fontSize: '10px', fontWeight: 700 }}>L</Avatar>
                                </Box>
                                <Box
                                    onClick={onClose}
                                    sx={{
                                        width: '28px',
                                        height: '28px',
                                        borderRadius: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        background: '#fef2f2',
                                        marginLeft: '8px',
                                        '&:hover': { background: '#fee2e2' }
                                    }}
                                >
                                    <CloseIcon sx={{ color: '#ef4444', fontSize: '16px' }} />
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Minutes Card - Compact */}
                    <Box sx={{
                        background: '#ffffff',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        flexShrink: 0
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <Box sx={{ width: '4px', height: '16px', background: 'linear-gradient(180deg, #6366f1 0%, #8b5cf6 100%)', borderRadius: '2px' }} />
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: 700, color: '#1a1b1d' }}>{t('MINUTES')}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: '16px' }}>
                            <Box sx={{ flex: 1, background: '#f8fafc', borderRadius: '10px', padding: '10px 12px' }}>
                                <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '6px', textAlign: 'center' }}>
                                    {t('All Games')}
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '11px', color: '#475569' }}>{t('Minutes')}</Typography>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#1e293b' }}>{!loading ? (playerState ? playerState['total_games_player_minutes'] : '0') : ''}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '11px', color: '#475569' }}>{t('Games')}</Typography>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#1e293b' }}>{!loading ? (playerState ? playerState['total_games_player_played'] : '0') : ''}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '11px', color: '#475569' }}>{t('Starting')}</Typography>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#1e293b' }}>{!loading ? (playerState ? playerState['total_games_player_starting'] : '0') : ''}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '11px', color: '#475569' }}>{t('Avg')}</Typography>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#1e293b' }}>{!loading ? (playerState ? parseInt(playerState['total_games_player_average_minutes']) : '0') : ''}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '6px', padding: '4px', background: '#e0e7ff', borderRadius: '6px' }}>
                                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#6366f1' }}>
                                        {!loading ? (playerState ? parseInt(playerState['total_games_player_percentage_minutes']) + '%' : '0') : ''} of total
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ flex: 1, background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', borderRadius: '10px', padding: '10px 12px', border: '1px solid #e9d5ff' }}>
                                <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', marginBottom: '6px', textAlign: 'center' }}>
                                    {t('Selected Games')}
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '11px', color: '#475569' }}>{t('Minutes')}</Typography>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#1e293b' }}>{!loading ? (playerState ? playerState['selected_games_player_minutes'] : '0') : ''}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '11px', color: '#475569' }}>{t('Games')}</Typography>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#1e293b' }}>{!loading ? (playerState ? playerState['selected_games_player_played'] : '0') : ''}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '11px', color: '#475569' }}>{t('Starting')}</Typography>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#1e293b' }}>{!loading ? (playerState ? playerState['selected_games_player_starting'] : '0') : ''}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '11px', color: '#475569' }}>{t('Avg')}</Typography>
                                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#1e293b' }}>{!loading ? (playerState ? parseInt(playerState['selected_games_player_average_minutes']) : '0') : ''}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '6px', padding: '4px', background: '#ddd6fe', borderRadius: '6px' }}>
                                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#7c3aed' }}>
                                        {!loading ? (playerState ? parseInt(playerState['selected_games_player_percentage_minutes']) + '%' : '0') : ''} of total
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Player Stats Card - Main Focus */}
                    <Box sx={{
                        background: '#ffffff',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexShrink: 0 }}>
                            <Box sx={{ width: '4px', height: '16px', background: 'linear-gradient(180deg, #6366f1 0%, #8b5cf6 100%)', borderRadius: '2px' }} />
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: 700, color: '#1a1b1d' }}>{t('PLAYER STATS')}</Typography>
                            <Typography sx={{ fontSize: '10px', color: '#64748b', marginLeft: 'auto' }}>{t('Click to view video')}</Typography>
                        </Box>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                                <CircularProgress size={40} sx={{ color: '#6366f1' }} />
                            </Box>
                        ) : (
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(5, 1fr)',
                                gap: '8px',
                                overflow: 'auto',
                                flex: 1,
                                alignContent: 'start',
                                paddingRight: '4px',
                                '&::-webkit-scrollbar': { width: '4px' },
                                '&::-webkit-scrollbar-thumb': { backgroundColor: '#e2e8f0', borderRadius: '2px' }
                            }}>
                                {(() => {
                                    const posId = currentPlayer?.player_position_id || currentPlayer?.position_id || player?.player_position_id || player?.position_id;
                                    const isGoalkeeper = posId === 16;
                                    if (isGoalkeeper) {
                                        return goalkeeper;
                                    }
                                    // Filter out goalkeeper-only stats for non-goalkeepers
                                    return statList.filter(item => !goalkeeperOnlyStats.includes(item.id));
                                })().map(
                                    (item, index) =>
                                        !arr_stat_profile.includes(item.id) &&
                                        playerState &&
                                        playerState[`total_${item.id}`] > 0 && (
                                            <Box
                                                key={index}
                                                onClick={loadingStatId === item.id ? null : handleDisplayVideo(item)}
                                                onContextMenu={loadingStatId === item.id ? null : handleExportTags(item)}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    padding: '12px 8px',
                                                    borderRadius: '8px',
                                                    background: '#ffffff',
                                                    border: '1px solid #e5e7eb',
                                                    cursor: loadingStatId === item.id ? 'not-allowed' : 'pointer',
                                                    opacity: loadingStatId === item.id ? 0.7 : 1,
                                                    transition: 'all 0.2s ease',
                                                    minHeight: '72px',
                                                    '&:hover': {
                                                        borderColor: '#6366f1',
                                                        background: '#f8fafc',
                                                        boxShadow: '0 2px 8px rgba(99, 102, 241, 0.12)'
                                                    }
                                                }}
                                            >
                                                {loadingStatId === item.id ? (
                                                    <CircularProgress size={18} sx={{ color: '#6366f1' }} />
                                                ) : (
                                                    <>
                                                        <Typography sx={{
                                                            fontFamily: "'DM Sans', sans-serif",
                                                            fontSize: '22px',
                                                            fontWeight: 700,
                                                            color: '#1e293b',
                                                            lineHeight: 1
                                                        }}>
                                                            {playerState ? playerState[`total_${item.id}`] : ''}
                                                        </Typography>
                                                        <Typography sx={{
                                                            fontFamily: "'DM Sans', sans-serif",
                                                            fontSize: '10px',
                                                            fontWeight: 600,
                                                            color: '#64748b',
                                                            textAlign: 'center',
                                                            marginTop: '4px',
                                                            lineHeight: 1.2
                                                        }}>
                                                            {t(item.title)}
                                                        </Typography>
                                                        {playerState && playerState.selected_games_player_played > 1 && (
                                                            <Typography sx={{
                                                                fontFamily: "'DM Sans', sans-serif",
                                                                fontSize: '9px',
                                                                fontWeight: 500,
                                                                color: '#6366f1',
                                                                marginTop: '3px',
                                                                background: '#f0f0ff',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px'
                                                            }}>
                                                                {playerState[`average_${item.id}`]}/game
                                                            </Typography>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        )
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
                </>
                </Box>
            </DialogContent>
            <PlayerStatErrorMessage open={errorOpen} message={errorText} onClose={() => setErrorOpen(false)} />
            <TeamStatsVideoPlayer
                t={t}
                open={videoOpen}
                onClose={(flag) => {
                    setVideoOpen(false);
                    if (flag) setRefresh((r) => !r);
                }}
                video_url={gameList}
                tagList={playData}
            />
            <GameExportToEdits t={t} open={exportOpen} onClose={() => setExportOpen(false)} tagList={playData} isTeams={false} />
            <PlayerEditDialog t={t} open={editOpen} onClose={() => { setEditOpen(false); setEditPlayerData(null); }} player={editPlayerData || player} onSave={handleUpdatePlayer} updatePlayersList={() => {}} />
        </Dialog>
    );
};

export default PlayerStatDialog;
